<!--
  - Copyright (c) 2025. Haodong JU
  -->

<template>
  <div class="member-selector">
    <div class="member-selector__filters mb-4">
      <v-row align="center" no-gutters>
        <!-- Search input -->
        <v-col cols="12" md="3" class="pr-2">
          <v-text-field
            v-model="searchQuery"
            label="搜索会员"
            prepend-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
            @input="onSearchInput"
          ></v-text-field>
        </v-col>

        <!-- Label filter -->
        <v-col cols="12" md="4" class="pr-2">
          <v-select
            v-model="selectedLabels"
            :items="availableLabels"
            label="会员标签"
            item-text="name"
            item-value="id"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-details
            @change="onLabelsChange"
          ></v-select>
        </v-col>

        <!-- Age filter -->
        <v-col cols="12" md="2" class="pr-2">
          <v-select
            v-model="ageFilter"
            :items="availableAges"
            label="年龄段"
            outlined
            dense
            clearable
            hide-details
            @change="onAgeChange"
          ></v-select>
        </v-col>

        <!-- Gender filter -->
        <v-col cols="12" md="2" class="pr-2">
          <v-select
            v-model="genderFilter"
            :items="availableGenders"
            label="性别"
            outlined
            dense
            clearable
            hide-details
            @change="onGenderChange"
          ></v-select>
        </v-col>

        <!-- Reset button -->
        <v-col cols="12" md="1">
          <v-btn
            small
            text
            color="primary"
            @click="resetFilters"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Members list -->
    <v-data-table
      :headers="headers"
      :items="filteredMembers"
      :loading="isLoading"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-options': [10, 20, 50],
        'items-per-page-text': '每页显示'
      }"
      class="elevation-1 member-selector__table"
      @click:row="toggleMemberSelection"
    >
      <!-- Selection column -->
      <template v-slot:item.selected="{ item }">
        <v-checkbox
          :input-value="isSelected(item.id)"
          @click.stop="toggleMemberSelection(item)"
          hide-details
          dense
        ></v-checkbox>
      </template>

      <!-- Username column -->
      <template v-slot:item.username="{ item }">
        <div class="d-flex align-center">
          <v-avatar size="32" class="mr-2">
            <v-img
              v-if="item.cloudUserInfo && item.cloudUserInfo.avatar"
              :src="item.cloudUserInfo.avatar"
              alt="User Avatar"
            ></v-img>
            <v-icon v-else>mdi-account</v-icon>
          </v-avatar>
          <div>
            <div>{{ item.username }}</div>
            <div class="caption grey--text">{{ item.email || item.phoneNumber }}</div>
          </div>
        </div>
      </template>

      <!-- Labels column -->
      <template v-slot:item.labels="{ item }">
        <v-chip
          v-for="label in (item.labels || [])"
          :key="label && label.id ? label.id : Math.random()"
          x-small
          class="mr-1 mb-1"
          :color="label && label.labelColor ? label.labelColor : 'primary'"
          text-color="white"
        >
          {{ label && label.labelName ? label.labelName : '' }}
        </v-chip>
      </template>

      <!-- Loading state -->
      <template v-slot:loading>
        <v-skeleton-loader
          type="table-row@6"
        ></v-skeleton-loader>
      </template>

      <!-- No data state -->
      <template v-slot:no-data>
        <div class="text-center pa-4">
          <v-icon large class="mb-2">mdi-account-off</v-icon>
          <div>没有找到符合条件的会员</div>
        </div>
      </template>
    </v-data-table>

    <!-- Selected members summary -->
    <div class="member-selector__selected mt-4 pa-2 rounded-lg">
      <div class="d-flex justify-space-between align-center">
        <div>
          <span class="font-weight-medium">已选择 {{ selectedMemberIds.length }} 位会员</span>
          <v-btn
            v-if="selectedMemberIds.length > 0"
            x-small
            text
            color="error"
            class="ml-2"
            @click="clearSelection"
          >
            <v-icon left x-small>mdi-close</v-icon>
            清除选择
          </v-btn>
          <v-btn
            v-if="filteredMembers.length > 0 && selectedMemberIds.length < filteredMembers.length"
            x-small
            text
            color="success"
            class="ml-2"
            @click="selectAllMembers"
          >
            <v-icon left x-small>mdi-checkbox-multiple-marked</v-icon>
            全选
          </v-btn>
        </div>
        <div>
          <v-btn
            color="primary"
            @click="confirmSelection"
            :disabled="selectedMemberIds.length === 0"
          >
            确认选择
          </v-btn>
        </div>
      </div>

      <!-- Selected members chips -->
      <div v-if="selectedMemberIds.length > 0" class="d-flex flex-wrap mt-2">
        <v-chip
          v-for="member in selectedMembers"
          :key="member.id"
          class="mr-1 mb-1"
          close
          @click:close="unselectMember(member.currentMembership.id)"
        >
          {{ member.username }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script>
import { useMemberSelectorStore } from '@/model/dataLayer/api/memberSelectorStore'

export default {
  name: 'MemberSelector',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: true
    },
    returnMode: {
      type: String,
      default: 'membership',
      validator: (value) => ['membership', 'user'].includes(value)
    }
  },
  data () {
    return {
      searchQuery: '',
      selectedLabels: [],
      ageFilter: '',
      genderFilter: '',
      availableLabels: [],
      availableAges: [],
      availableGenders: [],
      headers: [
        {
          text: '',
          value: 'selected',
          sortable: false,
          width: '50px'
        },
        {
          text: '会员',
          value: 'username',
          sortable: true
        },
        {
          text: '年龄段',
          value: 'ageDisplay',
          sortable: true
        },
        {
          text: '性别',
          value: 'gender',
          sortable: true
        },
        {
          text: '积分',
          value: 'currentPoints',
          sortable: true
        },
        {
          text: '上次消费',
          value: 'userLastConsumeAt',
          sortable: true
        },
        {
          text: '标签',
          value: 'labels',
          sortable: false
        }
      ]
    }
  },
  computed: {
    store () {
      return useMemberSelectorStore()
    },
    isLoading () {
      // Ensure we're accessing the value of the ref
      return this.store.isLoading.value
    },
    filteredMembers () {
      // When accessed from the component, the getter is wrapped in a ref object
      // We need to access the .value property to get the actual array
      return Array.isArray(this.store.getFilteredMembers.value) ? this.store.getFilteredMembers.value : []
    },
    selectedMemberIds () {
      // Access the value of the ref
      return this.store.selectedMemberIds.value
    },
    selectedMembers () {
      // When accessed from the component, the getter is wrapped in a ref object
      // We need to access the .value property to get the actual array
      return Array.isArray(this.store.getSelectedMembers.value) ? this.store.getSelectedMembers.value : []
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (newValue && Array.isArray(newValue)) {
          this.store.selectMembers(newValue)
        }
      }
    }
  },
  async mounted () {
    await this.fetchMembers()
    await this.fetchLabels()
    this.extractAvailableFilters()
  },
  methods: {
    async fetchMembers () {
      await this.store.fetchMembers()
    },
    async fetchLabels () {
      try {
        const { getLabelsForBusinessLayer } = await import('@/model/dataLayer/api/MembershipSystem/MembershipLabel')
        const response = await getLabelsForBusinessLayer()
        // Ensure labels is an array
        this.availableLabels = Array.isArray(response?.labels) ? response.labels : []
      } catch (error) {
        console.error('Error fetching labels:', error)
        this.availableLabels = []
      }
    },
    extractAvailableFilters () {
      // Extract unique age groups
      const ages = new Set()
      // Extract unique genders
      const genders = new Set()

      // Get the members array from the store
      // Since members is a ref, we need to access its value
      const members = this.store.members.value || []

      if (Array.isArray(members)) {
        members.forEach(member => {
          if (member.ageDisplay) ages.add(member.ageDisplay)
          if (member.gender) genders.add(member.gender)
        })
      }

      this.availableAges = Array.from(ages)
      this.availableGenders = Array.from(genders)
    },
    isSelected (memberId) {
      // Find the member by ID
      const member = this.store.members.value.find(m => m.id === memberId)
      if (!member || !member.currentMembership) return false

      // Use the membership ID instead of the user ID
      const membershipId = member.currentMembership.id

      // Use the computed property which already accesses .value
      return this.selectedMemberIds.includes(membershipId)
    },
    toggleMemberSelection (member) {
      if (!this.multiple) {
        // If not multiple, clear selection first
        this.store.clearSelection()
      }
      this.store.toggleMemberSelection(member.id)
    },
    unselectMember (memberId) {
      this.store.unselectMember(memberId)
    },
    clearSelection () {
      this.store.clearSelection()
    },
    selectAllMembers () {
      this.store.selectAll()
    },
    onSearchInput (value) {
      this.store.setSearchQuery(value)
    },
    onLabelsChange (value) {
      this.store.setSelectedLabels(value)
    },
    onAgeChange (value) {
      this.store.setAgeFilter(value)
    },
    onGenderChange (value) {
      this.store.setGenderFilter(value)
    },
    resetFilters () {
      this.searchQuery = ''
      this.selectedLabels = []
      this.ageFilter = ''
      this.genderFilter = ''
      this.store.resetFilters()
    },
    async confirmSelection () {
      const selectedIds = await this.store.getSelectedMemberIds(this.returnMode)
      this.$emit('input', selectedIds)
      this.$emit('selection-confirmed', selectedIds)
    }
  }
}
</script>

<style scoped>
.member-selector {
  width: 100%;
}

.member-selector__filters {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.member-selector__selected {
  border: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.member-selector__table {
  max-height: 500px;
  overflow-y: auto;
}
</style>
